import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSocket } from '../../contexts/SocketContext'
import { apiGet } from '../../utils/api'
import {
  sortRoster,
  type VisitorSearchResponse,
  type MapVisitor,
} from './visitorMap.helpers'

/** Page size for the initial roster fetch (backend caps at 500). */
const ROSTER_PAGE_SIZE = 300

/**
 * Owns the FULL roster of visitors for the map — everyone who has ever visited
 * within the retention window, online and offline.
 *
 * - Initial render: GET /api/visitors/search?pageSize=300 (no q / no status),
 *   which the backend returns already sorted online-first, then last_seen_at DESC.
 * - Stays live via socket events (no polling):
 *     visitor_online  → full MapVisitor (add if new, else flip offline→online)
 *     visitor_updated → full MapVisitor (merge fields on the matching visitor)
 *     visitor_offline → { id, disconnected_at } (mark offline, keep in the roster)
 *
 * The dashboard's SocketContext already joins the agent/queue rooms; the API
 * places agents in the `visitors` room. We emit `join_visitors` here too (it's
 * idempotent server-side) so the map works even if the legacy list hook that
 * normally emits it isn't mounted.
 */
export function useLiveVisitors() {
  const [visitors, setVisitors] = useState<MapVisitor[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { socket } = useSocket()

  const fetchRoster = useCallback(async () => {
    try {
      const data = await apiGet<VisitorSearchResponse>('/visitors/search', {
        pageSize: ROSTER_PAGE_SIZE,
      })
      setVisitors(sortRoster(data.visitors || []))
      setError(null)
    } catch (err) {
      console.error('Failed to load visitors:', err)
      setError('Failed to load visitors')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRoster()
  }, [fetchRoster])

  useEffect(() => {
    if (!socket) return

    socket.emit('join_visitors')

    // Upsert a visitor as ONLINE: add if new, otherwise flip the existing
    // (possibly offline) entry to online and refresh its fields.
    const upsertOnline = (incoming: MapVisitor) => {
      if (!incoming || !incoming.id) return
      setVisitors((prev) => {
        const idx = prev.findIndex((v) => v.id === incoming.id)
        const normalized: MapVisitor = {
          ...incoming,
          status: 'online',
          is_online: true,
        }
        const next =
          idx === -1
            ? [normalized, ...prev]
            : prev.map((v, i) => (i === idx ? { ...v, ...normalized } : v))
        return sortRoster(next)
      })
    }

    // Merge field updates onto the matching visitor (keep its online/offline
    // state as the payload reports it).
    const handleUpdated = (incoming: MapVisitor) => {
      if (!incoming || !incoming.id) return
      setVisitors((prev) => {
        const idx = prev.findIndex((v) => v.id === incoming.id)
        // Unknown visitor arriving via an update — add it as-is.
        const next =
          idx === -1
            ? [incoming, ...prev]
            : prev.map((v, i) => (i === idx ? { ...v, ...incoming } : v))
        return sortRoster(next)
      })
    }

    // Mark a visitor OFFLINE but KEEP it in the roster (don't drop it).
    const handleOffline = (payload: { id: string; disconnected_at?: string }) => {
      if (!payload?.id) return
      setVisitors((prev) => {
        const idx = prev.findIndex((v) => v.id === payload.id)
        if (idx === -1) return prev
        const next = prev.map((v, i) =>
          i === idx
            ? {
                ...v,
                status: 'offline' as const,
                is_online: false,
                disconnected_at: payload.disconnected_at ?? v.disconnected_at,
              }
            : v
        )
        return sortRoster(next)
      })
    }

    socket.on('visitor_online', upsertOnline)
    socket.on('visitor_updated', handleUpdated)
    socket.on('visitor_offline', handleOffline)

    return () => {
      socket.off('visitor_online', upsertOnline)
      socket.off('visitor_updated', handleUpdated)
      socket.off('visitor_offline', handleOffline)
    }
  }, [socket])

  const onlineCount = useMemo(
    () => visitors.filter((v) => v.status === 'online' || v.is_online).length,
    [visitors]
  )

  return { visitors, onlineCount, loading, error, refresh: fetchRoster }
}
